import React, { createContext, useContext, useState, useEffect } from 'react';
import { DEFAULT_THEME, getTheme } from '../config/themes';

const ThemeContext = createContext();

// Local storage key
const THEME_STORAGE_KEY = 'toolbone-theme';

/**
 * ThemeProvider Component
 * Manages theme state and provides theme switching functionality
 */
export const ThemeProvider = ({ children }) => {
  // Initialize theme from localStorage or use default
  const [currentTheme, setCurrentTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      const savedTheme = localStorage.getItem(THEME_STORAGE_KEY);
      return savedTheme || DEFAULT_THEME;
    }
    return DEFAULT_THEME;
  });

  // Apply theme to document
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Set data-theme attribute on document root
      document.documentElement.setAttribute('data-theme', currentTheme);

      // Save to localStorage
      localStorage.setItem(THEME_STORAGE_KEY, currentTheme);
    }
  }, [currentTheme]);

  /**
   * Change the current theme
   * @param {string} themeId - The ID of the theme to apply
   */
  const changeTheme = (themeId) => {
    const theme = getTheme(themeId);
    if (theme) {
      setCurrentTheme(themeId);
    }
  };

  /**
   * Get the current theme object
   * @returns {object} The current theme configuration
   */
  const getThemeConfig = () => {
    return getTheme(currentTheme);
  };

  const value = {
    currentTheme,
    changeTheme,
    getThemeConfig,
    themeId: currentTheme,
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};

/**
 * useTheme Hook
 * Access theme context in any component
 * @returns {object} Theme context value
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
